/**
 * Parameters for building Cue user message content.
 * Matches the params used in cue.service.ts:buildConversationMessages()
 *
 * Resource data (skills, collections, variables, deployments) is NOT included here.
 * The LLM queries resources on-demand via read tools (read_skills, read_variables,
 * read_collections, read_deployments) resolved server-side.
 */
export interface CueUserMessageParams {
  markdown: string;
  quote?: string;
  message?: string;
  isAutoMode?: boolean;
}

/**
 * Builds the user message content that Cue sends to the LLM.
 *
 * This function is SHARED between:
 * - Backend: cue.service.ts:buildConversationMessages()
 * - Frontend: debug-drawer.tsx (LLM Full Context tab)
 *
 * Contains: ==PROMPT==, ==MODE==, ==PART TO FOCUS ON==, ==REQUEST==
 * Does NOT contain resource sections — the LLM uses read tools for those.
 */
export function buildCueUserMessageContent(params: CueUserMessageParams): string {
  const { markdown, quote, message, isAutoMode } = params;

  // Build mode section
  const modeSection = isAutoMode
    ? "\n\n==MODE==\nAuto-accept is enabled. Suggestions will be automatically applied without user review. Prefer smaller, atomic edits."
    : "";

  // Build complete message with conditional quote and request sections
  if (quote) {
    return `==PROMPT==\n${markdown}${modeSection}\n\n==PART TO FOCUS ON==\n${quote}\n\n==REQUEST==\n${message ?? "[User's message would appear here]"}`;
  } else {
    return `==PROMPT==\n${markdown}${modeSection}\n\n==REQUEST==\n${message ?? "[User's message would appear here]"}`;
  }
}
